import axios from 'axios';

export type ErrorResponseData = { message?: unknown } | string | undefined;

// kvitka's SentryExceptionFilter (backend/src/common/filters/sentry-exception.filter.ts:23-31)
// sets `message` to `HttpException.getResponse()`, which is itself an object for a NestJS
// exception's default body shape -- the readable text then sits one level deeper, at
// `responseData.message.message`. Recurse exactly once for that; `maxDepth` bounds it so a
// malformed body can't recurse unboundedly. Kept private so no caller can pass a larger bound (or
// `Infinity`) and turn a cyclic body into a stack overflow.
const extractMessageFromResponseDataAtDepth = (
  responseData: ErrorResponseData,
  maxDepth: number,
): string | null => {
  if (typeof responseData === 'string' && responseData.trim().length > 0) {
    return responseData;
  }

  if (responseData && typeof responseData === 'object') {
    const maybeMessage = responseData.message;
    if (typeof maybeMessage === 'string' && maybeMessage.trim().length > 0) {
      return maybeMessage;
    }
    if (Array.isArray(maybeMessage)) {
      const strings = maybeMessage.filter(
        (item): item is string => typeof item === 'string' && item.trim().length > 0,
      );
      if (strings.length > 0) {
        return strings.join(', ');
      }
    }
    if (maxDepth > 0 && maybeMessage && typeof maybeMessage === 'object') {
      return extractMessageFromResponseDataAtDepth(maybeMessage as ErrorResponseData, maxDepth - 1);
    }
  }

  return null;
};

export const extractMessageFromResponseData = (responseData: ErrorResponseData): string | null =>
  extractMessageFromResponseDataAtDepth(responseData, 1);

/**
 * Extracts a human-readable error message from an unknown error.
 * Handles Axios errors, standard Error objects, and various response formats.
 */
export const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = extractMessageFromResponseData(error.response?.data);
    if (message) return message;
  }

  // Handle plain objects with response.data structure (e.g., mocked errors in tests)
  if (error && typeof error === 'object' && 'response' in error) {
    const errorWithResponse = error as { response?: { data?: ErrorResponseData } };
    const message = extractMessageFromResponseData(errorWithResponse.response?.data);
    if (message) return message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

/**
 * Reads the machine-readable business code off a failed API call.
 *
 * The backend's global exception filter nests the body under `message`, so the code lands at
 * `response.data.message.code`, not `response.data.code`. Must check the axios-error shape
 * first: `AxiosError` carries its own top-level `.code` (e.g. `'ERR_BAD_REQUEST'`) that would
 * otherwise shadow the real business code.
 */
export const getApiErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;

  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;

      const message = record.message;
      if (message && typeof message === 'object') {
        const messageCode = (message as Record<string, unknown>).code;
        if (typeof messageCode === 'string') return messageCode;
      }

      // Fallback in case the filter ever stops nesting under `message`.
      if (typeof record.code === 'string') return record.code;
    }
    return undefined;
  }

  // Not an axios error: the only other producer is a hook-annotated `.code` on the Error it
  // rethrows in place of the axios one.
  const direct = (error as { code?: unknown }).code;
  return typeof direct === 'string' ? direct : undefined;
};

export const getApiErrorStatus = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

/** The Error a mutation hook rethrows in place of the axios one, annotated with what the UI needs. */
export type ApiMutationError = Error & { status?: number; code?: string };

export const toApiMutationError = (error: unknown, message: string): ApiMutationError =>
  Object.assign(new Error(message), {
    status: getApiErrorStatus(error),
    code: getApiErrorCode(error),
  });
